<script lang="ts" setup>
import { computed, ref } from 'vue';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { useMutation } from '@vue/apollo-composable';
import { InviteRevokeDocument, InviteType } from '@/gql/graphql';

// --- Props for the component ---
interface Props {
  invite: InviteType;
  show: boolean;
}
const props = defineProps<Props>();
const emits = defineEmits(['success', 'close']);

// --- Computed Properties ---
const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Revoke Invite ---
const mutationShowSuccess = ref(false);
const mutationInProgress = ref(false);
const mutationError = ref<string | null>(null);
const {
  mutate: inviteMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(InviteRevokeDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.inviteRevoke) {
    console.debug('Invite revoked:', res.data.inviteRevoke);
    // emit event so the parent can refresh the memberships
    mutationShowSuccess.value = true;
    emits('success');
    // close the modal with delay
    setTimeout(() => {
      handleCloseModal();
    }, 2000);
  } else {
    console.error('Invite revoke failed:', res.data);
    mutationError.value = `Invite revoke failed: ${res.data}`;
  }
});

onMutationError((err) => {
  console.error('Error revoking invite:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function revokeInvite() {
  mutationError.value = null;
  mutationInProgress.value = true;

  if (!props.invite || !props.invite.id) {
    mutationInProgress.value = false;
    mutationError.value = 'Invalid invite data.';
    return;
  }

  inviteMutation({
    id: props.invite.id,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <b-modal
    v-if="props.invite.organization"
    v-model="show"
    @hide="handleCloseModal"
  >
    <!-- Title -->
    <template #title>
      <font-awesome-icon :icon="['fas', 'user-times']" class="me-2" />
      <strong>Revoke Invite</strong>
    </template>
    <!-- Content -->
    <div>
      <p>
        Confirm to revoke an invite to join
        <strong>{{ props.invite.organization.name }}</strong> organization.
      </p>

      <!-- Mutation Error Messages -->
      <BAlert v-model="mutationError" variant="danger" dismissible class="mt-3">
        {{ mutationError }}
      </BAlert>
    </div>

    <!-- Footer with action buttons -->
    <template #footer>
      <BButton variant="secondary" @click="handleCloseModal" class="me-2">
        <span>Cancel</span>
      </BButton>
      <BButton
        variant="danger"
        :disabled="mutationInProgress || mutationShowSuccess"
        @click="revokeInvite()"
      >
        <span v-if="mutationInProgress">
          <b-spinner small label="" class="me-1"></b-spinner>
          Revoking invite
        </span>
        <span v-else-if="mutationShowSuccess">
          <font-awesome-icon :icon="['fas', 'check-double']" class="me-1" />
          Invite revoked
        </span>
        <span v-else>
          <font-awesome-icon :icon="['fas', 'user-times']" class="me-1" />
          Revoke invite
        </span>
      </BButton>
    </template>
  </b-modal>
</template>
